import {
  DatastoreObjectType,
  dbObjectMeta,
  MapLike,
  MayContainMetaData,
  PayloadAttributeMetaData,
  TypeScopedValidCoreApplicationType,
} from '@iotv/iotv-v3-types'
import { ValidModelObject } from '../../classDependentTypes'
import { ModelCommon } from '../../model/ModelCommon'
import { enumerable } from '../constructs'

const className: 'DeviceConfig' = 'DeviceConfig'

const maxRecentStates: number = 5

export type ReportedConfig = MapLike<any> & { timestamp: number }
export type RequestedConfig = MapLike<any> & {
  broadcastScheduleSk?: string
  timestamp: number
  /** for AWS_LPWAN */
  MessageId?: string
}

export type DeviceConfigBase = MapLike<any> // & MayContainMetaData  & { messageType?: number }

export type DeviceConfigProps = MayContainMetaData & { messageType?: number } & {
  reported?: ReportedConfig
  requested?: RequestedConfig
  /** the previous reported configs limited by max states*/
  recentReported?: ReportedConfig[]
  /** the previous requested configs limited by max states*/
  recentRequested?: RequestedConfig[]
  maxRecentStates?: number
  lastReportedMs?: number
  lastRequestedMs?: number
  lastRequestedBroadcastScheduleSk?: string
}

export interface IDeviceConfig<ReportedConfigShape extends ReportedConfig = ReportedConfig, RequestedConfigShape extends RequestedConfig = RequestedConfig>
  extends DeviceConfigProps,
    DatastoreObjectType {
  metadata?: dbObjectMeta
  reported?: ReportedConfigShape
  requested?: RequestedConfigShape
  /** the previous reported configs limited by max states*/
  recentReported?: ReportedConfigShape[]
  /** the previous requested configs limited by max states*/
  recentRequested?: RequestedConfigShape[]
  lastReportedMs?: number
  lastRequestedMs?: number

  // recentStates?: { timestamp: number; reported: DC }[];
  lastRequestedBroadcastScheduleSk?: string
}

type ConfigUpdate = {
  reported?: ReportedConfig
  requested?: RequestedConfig
}

/**
 * @class Holds configuration object retrieved from device via downlink request
 * This is a pseudo property ie an instance should have pk as the sk of the device it belongs to,
 * And since it can only have one config, the instances sk should be a concaentation of pk + className
 */
export class DeviceConfig<ReportedConfigShape extends ReportedConfig = ReportedConfig, RequestedConfigShape extends RequestedConfig = RequestedConfig>
  extends ModelCommon
  implements IDeviceConfig<ReportedConfigShape, RequestedConfigShape>, TypeScopedValidCoreApplicationType<'DeviceConfig'>
{
  type: typeof className = 'DeviceConfig'
  metadata?: dbObjectMeta
  /** a combined map assigned to on each new reported state, giving the most recently reported full config from many individual updates */
  reported?: ReportedConfigShape
  /** a combined map assigned to on each new requested state, giving the most recently reported full config from many individual updates */
  requested?: RequestedConfigShape
  maxRecentStates: number

  /** the previous reported configs limited by max states*/
  recentReported: ReportedConfigShape[]
  /** the previous requested configs limited by max states*/
  recentRequested: RequestedConfigShape[]
  lastReportedMs?: number
  lastRequestedMs?: number
  lastRequestedBroadcastScheduleSk?: string

  constructor(ob: Partial<DeviceConfigProps> & DatastoreObjectType & { name?: string; id: string }) {
    super(className, { ...ob })
    this.maxRecentStates = ob.maxRecentStates ?? maxRecentStates
    this.metadata = ob.metadata // 2024 09 fix for overwriting property from ModelCommon -- this needs typing probably
    this.lastRequestedBroadcastScheduleSk = ob.lastRequestedBroadcastScheduleSk
    this.recentReported = ob.recentReported instanceof Array ? (ob.recentReported as ReportedConfigShape[]) : []
    this.recentRequested = ob.recentRequested instanceof Array ? (ob.recentRequested as RequestedConfigShape[]) : []

    /** temporary cleaning of exisiting data  */
    if (this.recentStates) {
      delete this.recentStates
    }
  }

  @enumerable(false)
  update = (update: ConfigUpdate, payloadAttributesMetaTypes?: MapLike<PayloadAttributeMetaData>) => {
    if (update.reported) {
      if (!this.reported) {
        this.reported = {} as ReportedConfigShape
      }

      const { meta, messageType, ...reportable } = this.reported
      this.recentReported.unshift(reportable as ReportedConfigShape)
      // trim
      if (this.recentReported.length > this.maxRecentStates) {
        this.recentReported.pop()
      }

      this.lastReportedMs = update.reported.timestamp

      /** merge to this.reported */
      Object.entries(update.reported).forEach(([k, v]) => {
        /** use transform is available */
        if (v && payloadAttributesMetaTypes?.[k]?.transformIn) {
          if (this.reported && !this.reported[k]) {
            ;(this.reported as { [k: string]: any })[k] = {}
          }
          const existingValue = this.reported?.[k]
          const update = payloadAttributesMetaTypes[k].transformIn?.(v, existingValue)
          if (update) {
            ;(this.reported as { [k: string]: any })![k] = update
          }
        } else {
          ;(this.reported as { [k: string]: any })![k] = v
        }
      })
    }

    if (update.requested) {
      if (update.requested.broadcastScheduleSk) {
        this.lastRequestedBroadcastScheduleSk = update.requested.broadcastScheduleSk
      }

      this.lastRequestedMs = update.requested.timestamp
      this.requested = this.requested ? Object.assign(this.requested, update.requested) : (update.requested as RequestedConfigShape)

      this.recentRequested.unshift(update.requested as RequestedConfigShape)
      if (this.recentRequested.length > maxRecentStates) {
        this.recentRequested.pop()
      }
    }

    this.metadata = { ...this.metadata, lastUpdatedMs: Date.now() }
  }

  static createForDevice(device: ValidModelObject<'Device'>) {
    const sk = `${device.sk}_${className}`
    const pk = device.pk
    const name = `${device.name ?? device.sk} DeviceConfig`
    const id = `${device.id}_${className}`

    return new DeviceConfig({ sk, pk, name, id })
  }
}
